import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../core/routes/routes";
import { useSpeedManagmentQuery } from "./hooks/useSpeedManagmentQuery";
import { useSpeedManagmentCommand } from "./hooks/useSpeedManagmentCommand";
import IconButton from "../../common/components/IconButton";
import FormSpeedManagment from "./components/FormSpeedManagment";
import {
  BsClipboardPlus,
  BsFileSpreadsheet,
  BsPencilSquare,
  BsTrash,
} from "react-icons/bs";

const NUMERIC_FIELDS = [
  "ownInProgram",
  "ownUsedMonth",
  "contractorsInProgram",
  "contractorsUsedMonth",
  "thirdPartiesInProgram",
  "thirdPartiesUsedMonth",
];

const MONTH_NAMES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

function formatMonth(monthKey) {
  const [year, month] = monthKey.split("-");
  return `${MONTH_NAMES[parseInt(month, 10) - 1]} ${year}`;
}

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function monthKeyFromItem(item) {
  const raw = item.date || item.createdAt;
  if (!raw) return "sin-mes";
  // Solo YYYY-MM; evita corrimientos por ISO/UTC (ej. ...T05:00:00.000Z)
  const match = String(raw).match(/(\d{4})-(\d{2})/);
  return match ? `${match[1]}-${match[2]}` : "sin-mes";
}

function monthToStartDate(ym) {
  if (!ym) return "";
  return `${ym}-01`;
}

function monthToEndDate(ym) {
  if (!ym) return "";
  const [year, month] = ym.split("-").map(Number);
  const lastDay = new Date(year, month, 0).getDate();
  return `${ym}-${String(lastDay).padStart(2, "0")}`;
}

function toAppliedFilters({ startMonth, endMonth }) {
  return {
    startDate: monthToStartDate(startMonth),
    endDate: monthToEndDate(endMonth),
  };
}

function ListSpeedManagment() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ startMonth: "", endMonth: "" });
  const [appliedFilters, setAppliedFilters] = useState({
    startDate: "",
    endDate: "",
  });
  const { speedManagmentData, loading, refresh } =
    useSpeedManagmentQuery(appliedFilters);
  const {
    exportToExcel,
    deleteSpeedManagment,
    updateSpeedManagment,
    speedManagment,
    handleChange,
    setDefaultValues,
    errors: { errors },
    loading: commandLoading,
  } = useSpeedManagmentCommand(refresh);

  const [showEditModal, setShowEditModal] = useState(false);

  const handleFilter = () => {
    setAppliedFilters(toAppliedFilters(filters));
  };

  const handleEdit = (item) => {
    setDefaultValues(item);
    setShowEditModal(true);
  };

  const handleCloseEdit = () => {
    setShowEditModal(false);
    setDefaultValues(null);
  };

  const handleUpdate = async () => {
    const success = await updateSpeedManagment();
    if (success) {
      handleCloseEdit();
    }
  };

  const tableRows = useMemo(() => {
    const monthGroups = new Map();

    for (const item of speedManagmentData) {
      const key = monthKeyFromItem(item);
      if (!monthGroups.has(key)) {
        monthGroups.set(key, []);
      }
      monthGroups.get(key).push(item);
    }

    // Agrupar meses por año (orden de aparición ya viene desc por fecha)
    const yearGroups = new Map();
    for (const [monthKey, items] of monthGroups) {
      const year = monthKey.split("-")[0];
      if (!yearGroups.has(year)) {
        yearGroups.set(year, []);
      }
      yearGroups.get(year).push({ monthKey, items });
    }

    const rows = [];
    for (const [year, months] of yearGroups) {
      const yearItems = months.flatMap((m) => m.items);
      const yearTotals = NUMERIC_FIELDS.reduce((acc, field) => {
        acc[field] = yearItems.reduce(
          (sum, row) => sum + toNumber(row[field]),
          0
        );
        return acc;
      }, {});

      for (const { monthKey, items } of months) {
        const monthRowSpan = items.length;
        items.forEach((item, index) => {
          rows.push({
            key: `row-${item.id}`,
            isFirst: index === 0,
            isTotal: false,
            monthRowSpan,
            month: formatMonth(monthKey),
            record: item,
            ...NUMERIC_FIELDS.reduce((acc, field) => {
              acc[field] = item[field];
              return acc;
            }, {}),
          });
        });
      }

      rows.push({
        key: `total-${year}`,
        isFirst: true,
        isTotal: true,
        monthRowSpan: 1,
        month: `Total ${year}`,
        record: null,
        ...yearTotals,
      });
    }

    return rows;
  }, [speedManagmentData]);

  return (
    <main className="container-fluid p-4">
      <style>{`
        .sv-table-wrap {
          overflow-x: auto;
          font-family: Arial, sans-serif;
          max-width: 100%;
        }
        .sv-table {
          width: 100%;
          border-collapse: collapse;
        }
        .sv-table th {
          padding: 12px 10px;
          text-align: left;
          font-weight: normal;
          font-size: 14px;
          color: #6b7280;
          border-bottom: 1px solid #e5e7eb;
          border-right: 1px solid #e5e7eb;
          background-color: #f4f4f4;
        }
        .sv-table th:last-child,
        .sv-table td:last-child {
          border-right: none;
        }
        .sv-table td {
          padding: 12px 10px;
          font-size: 13px;
          color: #111827;
          border-bottom: 1px solid #e5e7eb;
          border-right: 1px solid #e5e7eb;
        }
        .sv-table tbody tr:hover {
          background-color: #f9fafb;
        }
        .sv-cell-month {
          font-weight: 600;
          vertical-align: middle;
          white-space: nowrap;
        }
        .sv-cell-total {
          font-weight: 700;
        }
      `}</style>

      <header className="d-flex justify-content-between align-items-center mb-4 px-3 py-2 border-bottom">
        <h1 className="h2 m-0 fw-bold">Gestión de velocidad</h1>
        <div className="d-flex gap-2">
          <IconButton
            tooltipText={"Registrar nuevo"}
            onClick={() => navigate(ROUTES.REGISTER_SPEED_MANAGMENT)}
          >
            <BsClipboardPlus size={24} />
          </IconButton>
          <IconButton
            tooltipText={"Exportar"}
            onClick={() => exportToExcel(appliedFilters)}
          >
            <BsFileSpreadsheet size={24} />
          </IconButton>
        </div>
      </header>

      <section className="shadow w-100 d-flex flex-column">
        <div className="d-flex gap-3 justify-content-center justify-content-md-end flex-wrap align-items-end mb-3">
          <div className="mb-3">
            <label className="form-label">Mes inicio</label>
            <input
              type="month"
              className="form-control"
              value={filters.startMonth}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  startMonth: e.target.value,
                }))
              }
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Mes fin</label>
            <input
              type="month"
              className="form-control"
              value={filters.endMonth}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  endMonth: e.target.value,
                }))
              }
            />
          </div>
          <div className="mb-3">
            <label className="form-label d-block">&nbsp;</label>
            <button
              type="button"
              className="btn btn-dark"
              style={{ height: 38, whiteSpace: "nowrap" }}
              onClick={handleFilter}
            >
              Filtrar
            </button>
          </div>
        </div>

        <div className="sv-table-wrap">
          <table className="sv-table">
            <thead>
              <tr>
                <th>Mes</th>
                <th>Vehículos propios en programa</th>
                <th>Vehículos propios usados en el mes</th>
                <th>Vehículos contratistas en programa</th>
                <th>Vehículos contratistas usados en el mes</th>
                <th>Terceros en el programa</th>
                <th>Vehículos terceros usados en el mes</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-4 text-muted">
                    Cargando datos...
                  </td>
                </tr>
              ) : tableRows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-4 text-muted">
                    No hay datos para mostrar.
                  </td>
                </tr>
              ) : (
                tableRows.map((row) => (
                  <tr key={row.key}>
                    {row.isFirst && (
                      <td
                        rowSpan={row.monthRowSpan}
                        className={
                          row.isTotal ? "sv-cell-total" : "sv-cell-month"
                        }
                      >
                        {row.isTotal ? (
                          <strong>{row.month}</strong>
                        ) : (
                          row.month
                        )}
                      </td>
                    )}
                    {NUMERIC_FIELDS.map((field) => (
                      <td
                        key={field}
                        className={row.isTotal ? "sv-cell-total" : undefined}
                      >
                        {row.isTotal ? (
                          <strong>{row[field]}</strong>
                        ) : (
                          row[field]
                        )}
                      </td>
                    ))}
                    <td>
                      {row.isTotal ? null : (
                        <div className="d-flex align-items-center gap-2">
                          <IconButton
                            tooltipText="Editar"
                            onClick={() => handleEdit(row.record)}
                          >
                            <BsPencilSquare size={20} />
                          </IconButton>
                          <IconButton
                            tooltipText="Eliminar"
                            onClick={() => deleteSpeedManagment(row.record.id)}
                          >
                            <BsTrash size={20} color="#dc3545" />
                          </IconButton>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {showEditModal && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
          onClick={handleCloseEdit}
        >
          <div
            className="bg-white rounded shadow p-4"
            style={{ maxWidth: 700, width: "100%", maxHeight: "90vh", overflowY: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-3">Editar gestión de velocidad</h3>
            <FormSpeedManagment
              speedManagment={speedManagment}
              onChange={handleChange}
              errors={errors}
              onSubmit={handleUpdate}
              onCancel={handleCloseEdit}
              submitLabel="Guardar"
              loading={commandLoading}
            />
          </div>
        </div>
      )}
    </main>
  );
}

export default ListSpeedManagment;
